import rp from '@reporting/rp-app-host';
import { Form, FormAdapter, FormAlert, FormContainerOptions, FormControlProperties, FormControlType, FormValues } from '@omegagrid/form';
import { dom } from '@omegagrid/core';
import { msg } from "@omegagrid/localize";
import { Accounting, User, Application, AccountingRights } from '@reporting/rp-types/core';
import { render, html } from 'lit';

export class AccessTokenFormAdapter extends FormAdapter {

	private users: User[];
	private accountings: Accounting[];
	private applications: Application[];
	private showCustomExpControl: boolean = false;

	getColumns() {
		return [
			{width: 180},
			{width: null},
		];
	}

	async getControls(): Promise<FormControlProperties[]> {
		if (!this.users) {
			[this.users, this.accountings, this.applications] = await Promise.all([
				rp.ms('users').get<User[]>('groupUsers'),
				rp.ms('accounting').get<Accounting[]>(
					`accountings/${rp.accountingProvider.accounting.id_accounting_group}`,
					{ right: AccountingRights.Admin }
				),
				rp.ms('accounting').get<Application[]>('clientCredentials')
			]);
		}

		// a token can never exercise more than its issuer holds, so do not offer more either
		const scopes = Object.keys(AccountingRights)
			.filter(r => rp.userProvider.hasRight((AccountingRights as any)[r]))
			.map(r => ({k: (AccountingRights as any)[r], v: r}));
		scopes.sort((a, b) => Number(b.k.startsWith('io.')) - Number(a.k.startsWith('io.')) || a.k.localeCompare(b.k));

		let r = 0;
		const controls: FormControlProperties[] = [
			{row: r, col: 0, type: FormControlType.Label, value: msg('Name'), for: 'name'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'name', required: true, pattern: /^.{0,50}$/},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Company'), for: 'accounting'},
			{row: r++, col: 1, type: FormControlType.List, key: 'accounting', required: true, content: {
				sourceData: this.accountings.map(a => ({ k: a.accounting, v: `${a.accounting} ${a.company}` })),
				options: { selectMode: 'single', autoClose: true }
			}},
			{row: r, col: 0, type: FormControlType.Label, value: msg('User'), for: 'user_name'},
			{row: r++, col: 1, type: FormControlType.List, key: 'user_name', required: false, content: {
				sourceData: this.users.map(u => ({ k: u.user_name, v: u.login_name || u.user_name })),
				options: { selectMode: 'single', autoClose: true }
			}},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Application'), for: 'id_application'},
			{row: r++, col: 1, type: FormControlType.List, key: 'id_application', required: false, content: {
				sourceData: this.applications.map(app => ({k: app.id_application, v: `${app.id_application} ${app.name}`})),
				options: { selectMode: 'single', autoClose: true }
			}},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Scope'), for: 'scope'},
			{row: r++, col: 1, type: FormControlType.List, key: 'scope', required: true, content: {
				sourceData: scopes, options: {selectMode: 'multiple', autoClose: false}
			}},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Expiration'), for: '_expiration'},
			{row: r++, col: 1, type: FormControlType.List, value: 7, key: '_expiration', content: {
				sourceData: [
					{ k: 7, v: `7 ${msg('Days')}` },
					{ k: 30, v: `30 ${msg('Days')}` },
					{ k: 60, v: `60 ${msg('Days')}` },
					{ k: 90, v: `90 ${msg('Days')}` },
					{ k: 0, v: msg('Never') },
					{ k: -1, v: msg('Custom date') },
				], options: { selectMode: 'single', autoClose: true }
			}},
		];
		if (this.showCustomExpControl) {
			const minDate = new Date().toISOString();
			controls.push(
				{row: r, col: 0, type: FormControlType.Label, value: msg('Custom expiration'), for: '_custom_expiration'},
				{row: r++, col: 1, type: FormControlType.CalendarDateInput, key: '_custom_expiration', required: true, options: { minDate }}
			);
		}
		return controls;
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: ["save", "close"]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		const app = this.applications.find(a => a.id_application === formValues['id_application']) ?? null;

		// no user = a token for the application itself, so an application that acts without one
		// has to be selected; its scope and the issuer's own rights are then the only bounds
		if (!formValues['user_name']) {
			if (!app) throw Error(msg('User or application is required'));
			if (app.requires_user) throw Error(msg('User is required for the selected application'));
		}

		const tokenInfo = await rp.auth.client.issueToken({
			name: formValues.name as string,
			username: formValues.user_name as string,
			accounting: formValues.accounting as number,
			expiration: this.calculateExpiration(formValues._expiration as number, formValues._custom_expiration as Date),
			client_id: app?.client_id,
			scope: (formValues.scope as any[])?.map(([first]) => first) as string[] || null,
		});

		if (!tokenInfo || (tokenInfo as any).error) {
			throw Error(`Failed to create token: ${(tokenInfo as any)?.error || 'Unknown error'}`);
		}
		
		this.showToken(tokenInfo.access_token);

		return true;
	}

	showToken(token: string) {
		const div = dom.createElement<HTMLDivElement>('div', {style: {
			margin: '15px', 
			userSelect: 'text',
			overflow: 'hidden'
		}});
		
		render(html`
			<div style="display: flex; flex-direction: row; align-items: stretch; width: 100%; height: 20px">
				<input
					readonly
					style="flex: 1; background: transparent; border: 1px solid var(--og-accent-color); outline: none; color: var(--og-text-color)"
					.value="${token}" />
				<og-button
					style="flex: 0 0 20px"
					icon="copy"
					title="${msg('Copy to clipboard')}"
					@click=${() => navigator.clipboard.writeText(token)}>
				</og-button>
			</div>

			<div>
				⚠️ ${msg('Please copy and store the Access Token now.')}
				${msg('For security reasons, you will not be able to view it again.')}
			</div>
		`, div);

		rp.host.bucket.openDialog({
			component: div,
			caption: msg('New access token created'),
			closable: true,
			width: 750,
			height: 250,
			resizable: true,
			location: 'center',
		});
	}

	calculateExpiration(days: number, customExpiration?: Date): number | null {
		if (days === -1 && customExpiration) {
			const now = Date.now();
			const diffMs = customExpiration.getTime() - now;
			return diffMs > 0 ? Math.floor(diffMs / 1000) : 0;
		}
		if (days <= 0) return 0;
		return days * 24 * 60 * 60;
	}

	updateValues = (controls: FormControlProperties[], values: FormValues) => {
		if (!values) return controls;
		const map = new Map(controls.filter(item => item.key).map(item => [item.key, item]));
		Object.entries(values).forEach(([k, v]) => Object.assign(map.get(k) || {}, { value: v }));
		return controls;
	};

	async updateFormControls(form: Form) {
		const showCustomExp = form.values._expiration ? (form.values._expiration as number) === -1 : false;
		if (showCustomExp !== this.showCustomExpControl) {
			this.showCustomExpControl = showCustomExp;
			form.setSourceData({
				controls: this.updateValues(await this.getControls(), form.values),
				columns: this.getColumns()
			});
		}
	}

};
